import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight,
    Play,
    Crown,
    Sparkles,
    ScrollText,
    Quote,
    PenTool,
    BookOpen,
    Shield,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import useSeo from "../lib/useSeo";
import DiscoverySection from "../components/DiscoverySection";

const HERO_BG =
    "https://static.prod-images.emergentagent.com/jobs/3101a8e0-2797-4c28-8b52-211eac3258b8/images/4a894e998954f1e2cb2c5e109aa4a3c66b1c419224da0628f3440c48599194c8.png";

export default function HomePage() {
    useSeo({
        title: "Sovereign Commander",
        description:
            "EIGOO Books Hub — home of Sovereign Commander, the flagship animated comic. Read in cinematic page mode or scroll-driven motion-comic mode. New chapters, collector editions, and more.",
    });
    const [comics, setComics] = useState([]);
    const [flagship, setFlagship] = useState(null);
    const [preorderState, setPreorderState] = useState({ type: "idle", msg: "" });
    const [preorderForm, setPreorderForm] = useState({
        name: "",
        email: "",
        edition: "collector",
        quantity: 1,
    });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/comics");
                setComics(data);
                const flag = data.find((c) => c.is_flagship);
                if (flag) {
                    const { data: full } = await api.get(`/comics/${flag.slug}`);
                    setFlagship(full);
                }
            } catch (e) {
                console.error(e);
            }
        })();
    }, []);

    const submitPreorder = async (e) => {
        e.preventDefault();
        setPreorderState({ type: "loading", msg: "" });
        try {
            await api.post("/preorder", preorderForm);
            setPreorderState({
                type: "ok",
                msg: "Reserved. Your name is etched into the ledger.",
            });
            setPreorderForm({ name: "", email: "", edition: "collector", quantity: 1 });
        } catch (err) {
            setPreorderState({
                type: "err",
                msg: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        }
    };

    return (
        <main data-testid="home-page" className="bg-[#0a0a0a] text-white">
            {/* HERO — Publishing company positioning */}
            <section data-testid="hero-section" className="relative overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url(${HERO_BG})`,
                        filter: "saturate(1.0) brightness(0.55)",
                    }}
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(10,10,10,0.78) 0%, rgba(10,10,10,0.85) 50%, rgba(10,10,10,1) 100%)",
                    }}
                />
                <div className="absolute inset-0 spotlight" />

                <div className="relative max-w-6xl mx-auto px-6 lg:px-10 pt-28 pb-24 lg:pt-36 lg:pb-32 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="flex items-center justify-center gap-3 mb-8">
                            <div className="h-px w-12 bg-[#D4AF37]" />
                            <span className="eyebrow">EIGOO Books · Publishing Company</span>
                            <div className="h-px w-12 bg-[#D4AF37]" />
                        </div>

                        <h1
                            data-testid="hero-headline"
                            className="font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.98] tracking-tight"
                        >
                            Publish. Earn.
                            <br />
                            <span className="text-gold-gradient italic font-light">
                                Own your story.
                            </span>
                        </h1>

                        <p className="mt-10 text-lg md:text-xl text-[#cfcfcf] max-w-2xl mx-auto leading-relaxed font-light">
                            EIGOO Books is a publishing imprint for writers who want distribution
                            and marketing without surrendering their work. 50–60% royalty.
                            Your copyright stays yours. Your audience stays yours.
                        </p>

                        <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
                            <Link
                                data-testid="hero-publish-cta"
                                to="/publish"
                                className="btn-gold"
                            >
                                <PenTool className="w-3.5 h-3.5" /> Publish With Us
                            </Link>
                            <Link
                                data-testid="hero-library-cta"
                                to="/library"
                                className="btn-ghost-gold"
                            >
                                <BookOpen className="w-3.5 h-3.5" /> For Readers
                            </Link>
                        </div>

                        <div className="mt-16 grid grid-cols-3 gap-6 max-w-xl mx-auto">
                            {[
                                { v: "50–60%", l: "Royalty split" },
                                { v: "100%", l: "You keep your IP" },
                                { v: "3", l: "Tiers to grow" },
                            ].map((s) => (
                                <div key={s.l}>
                                    <div className="font-serif text-3xl md:text-4xl text-gold-gradient">
                                        {s.v}
                                    </div>
                                    <div className="eyebrow mt-2 text-[10px]">{s.l}</div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-px gold-rule" />
            </section>

            {/* FOR WRITERS / FOR READERS SPLIT */}
            <section
                data-testid="audience-split"
                className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-2 gap-6"
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="frame-gold p-10"
                >
                    <PenTool className="w-6 h-6 text-[#D4AF37] mb-6" />
                    <div className="eyebrow mb-3">For Writers</div>
                    <h3 className="font-serif text-3xl md:text-4xl leading-snug mb-4">
                        Keep what you wrote.{" "}
                        <span className="text-[#E6D59A] italic">Earn what you made.</span>
                    </h3>
                    <p className="text-[#A1A1AA] mb-8 leading-relaxed">
                        Three tiers. Clear royalties. We handle production, listing, and marketing.
                        You handle the story. Your IP stays yours — always.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Link to="/publish" className="btn-gold !py-2.5 !px-5" data-testid="split-publish-cta">
                            See the tiers <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                        <Link
                            to="/apply"
                            className="btn-ghost-gold !py-2.5 !px-5"
                            data-testid="split-apply-cta"
                        >
                            Apply to publish
                        </Link>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="frame-gold p-10"
                >
                    <BookOpen className="w-6 h-6 text-[#D4AF37] mb-6" />
                    <div className="eyebrow mb-3">For Readers</div>
                    <h3 className="font-serif text-3xl md:text-4xl leading-snug mb-4">
                        Stories pressed in obsidian,{" "}
                        <span className="text-[#E6D59A] italic">bound in gold.</span>
                    </h3>
                    <p className="text-[#A1A1AA] mb-8 leading-relaxed">
                        Begin with Sovereign Commander — our flagship animated series — then
                        browse the full library of EIGOO imprint titles.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        {flagship?.chapters?.[0] && (
                            <Link
                                data-testid="split-read-cta"
                                to={`/read/${flagship.chapters[0].id}`}
                                className="btn-gold !py-2.5 !px-5"
                            >
                                <Play className="w-3.5 h-3.5" /> Begin Chapter I
                            </Link>
                        )}
                        <Link
                            to="/library"
                            className="btn-ghost-gold !py-2.5 !px-5"
                            data-testid="split-library-cta"
                        >
                            The Library
                        </Link>
                    </div>
                </motion.div>
            </section>

            {/* SOVEREIGN COMMANDER SHOWCASE (moved below hero) */}
            <section
                data-testid="sovereign-showcase"
                className="relative border-y"
                style={{ borderColor: "rgba(212,175,55,0.18)", background: "#070707" }}
            >
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-25"
                    style={{ backgroundImage: `url(${HERO_BG})` }}
                />
                <div className="absolute inset-0 bg-[#070707]/80" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-7">
                        <div className="eyebrow mb-4 flex items-center gap-2">
                            <Crown className="w-3.5 h-3.5" /> Flagship title
                        </div>
                        <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
                            Sovereign{" "}
                            <span className="text-gold-gradient italic font-light">Commander</span>
                        </h2>
                        <p className="mt-6 text-lg text-[#cfcfcf] max-w-xl leading-relaxed">
                            He bears the crown that bleeds. An animated comic from the obsidian
                            halls of EIGOO Books — read in cinematic page mode, or scroll through
                            the full motion-comic panel sequence.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            {flagship?.chapters?.[0] && (
                                <Link
                                    data-testid="hero-read-now"
                                    to={`/read/${flagship.chapters[0].id}`}
                                    className="btn-gold"
                                >
                                    <Play className="w-3.5 h-3.5" /> Begin Chapter I
                                </Link>
                            )}
                            <Link
                                data-testid="hero-view-series"
                                to="/comic/sovereign-commander"
                                className="btn-ghost-gold"
                            >
                                View the Series <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                    </div>
                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.0 }}
                        className="md:col-span-5 hidden md:block"
                    >
                        <div
                            className="frame-gold p-1"
                            style={{ aspectRatio: "3/4", maxWidth: 360, marginLeft: "auto" }}
                        >
                            <div
                                className="w-full h-full bg-cover bg-center"
                                style={{ backgroundImage: `url(${HERO_BG})` }}
                            />
                        </div>
                        <div className="text-right mt-4">
                            <div className="eyebrow">Issue No. 01 · Volume I</div>
                            <div className="font-serif italic text-[#E6D59A] text-lg">
                                The Crown That Bleeds
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* QUOTE / EDITORIAL */}
            <section className="max-w-5xl mx-auto px-6 lg:px-10 py-24 text-center">
                <Quote className="w-8 h-8 text-[#D4AF37] mx-auto mb-6 opacity-60" />
                <p className="font-serif italic text-2xl md:text-4xl leading-snug text-[#E6D59A]">
                    &ldquo;EIGOO does not publish stories. It commissions them — pressed in obsidian,
                    bound in gold, animated for the eye that still demands ceremony.&rdquo;
                </p>
                <div className="eyebrow mt-8">— The House Imprint</div>
            </section>

            {/* CATALOG GRID */}
            <section
                data-testid="catalog-section"
                className="max-w-7xl mx-auto px-6 lg:px-10 pb-32"
            >
                <div className="flex items-end justify-between mb-12">
                    <div>
                        <div className="eyebrow mb-3">The Library</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-white">
                            Currently in <span className="text-gold-gradient">circulation</span>
                        </h2>
                    </div>
                    <Link
                        to="/library"
                        data-testid="see-all-link"
                        className="hidden md:inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#E6D59A] uppercase tracking-widest text-xs"
                    >
                        See all titles <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {comics.map((c, i) => (
                        <motion.div
                            key={c.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7, delay: i * 0.1 }}
                        >
                            <Link
                                to={`/comic/${c.slug}`}
                                data-testid={`catalog-card-${c.slug}`}
                                className="block frame-gold p-6 group"
                            >
                                <div
                                    className="aspect-[3/4] bg-cover bg-center mb-6 relative overflow-hidden"
                                    style={{ backgroundImage: `url(${c.cover_url})` }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                                    {c.is_flagship && (
                                        <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-[#0a0a0a]/80 px-2.5 py-1 border border-[#D4AF37]/40">
                                            <Crown className="w-3 h-3 text-[#D4AF37]" />
                                            <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]">
                                                Flagship
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="eyebrow text-[10px] mb-2">
                                    {c.chapter_count} chapter{c.chapter_count === 1 ? "" : "s"} · Animated
                                </div>
                                <h3 className="font-serif text-2xl text-white group-hover:text-[#E6D59A] transition-colors">
                                    {c.title}
                                </h3>
                                <p className="italic text-[#A1A1AA] text-sm mt-2 leading-snug">
                                    {c.tagline}
                                </p>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* DISCOVERY SESSION — $97 CHECKOUT */}
            <DiscoverySection />

            {/* PRE-ORDER CTA */}
            <section
                data-testid="preorder-section"
                className="relative border-y"
                style={{ borderColor: "rgba(212,175,55,0.18)" }}
            >
                <div
                    className="absolute inset-0 opacity-30"
                    style={{
                        backgroundImage: `url(https://images.unsplash.com/photo-1761437855740-c894da924d79?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80)`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="absolute inset-0 bg-[#0a0a0a]/85" />

                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-12 gap-16 items-start">
                    <div className="md:col-span-6">
                        <div className="eyebrow mb-4 flex items-center gap-2">
                            <Sparkles className="w-3.5 h-3.5" /> Collector Edition · Pre-order
                        </div>
                        <h2 className="font-serif text-4xl md:text-6xl leading-tight">
                            Bind your name into <br />
                            the <span className="text-gold-gradient italic">first impression</span>.
                        </h2>
                        <p className="text-[#A1A1AA] mt-6 max-w-md leading-relaxed">
                            Volume I of Sovereign Commander — pressed on archival stock, foiled in
                            true 23-karat gold, with seven exclusive panels not in the digital release.
                            Limited to 1,000 numbered impressions worldwide.
                        </p>
                        <ul className="mt-8 space-y-3 text-sm text-[#cfcfcf]">
                            {[
                                "Hand-numbered colophon, signed by the imprint",
                                "Seven exclusive panels & a fold-out gilded map",
                                "Free digital animated edition included",
                            ].map((b) => (
                                <li key={b} className="flex items-start gap-3">
                                    <ScrollText className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <form
                        onSubmit={submitPreorder}
                        data-testid="preorder-form"
                        className="md:col-span-6 frame-gold p-8 md:p-10 space-y-5"
                    >
                        <div className="flex items-baseline justify-between">
                            <span className="font-serif text-3xl text-gold-gradient">
                                Reserve a Copy
                            </span>
                            <span className="eyebrow text-[10px]">No payment yet</span>
                        </div>
                        <div className="gold-rule" />

                        <div className="space-y-1.5">
                            <label className="eyebrow text-[10px] block">Name</label>
                            <input
                                data-testid="preorder-name"
                                required
                                value={preorderForm.name}
                                onChange={(e) =>
                                    setPreorderForm({ ...preorderForm, name: e.target.value })
                                }
                                className="w-full bg-[#141414] border px-4 py-3 text-white"
                                style={{ borderColor: "rgba(212,175,55,0.2)" }}
                            />
                        </div>

                        <div className="space-y-1.5">
                            <label className="eyebrow text-[10px] block">Email</label>
                            <input
                                data-testid="preorder-email"
                                required
                                type="email"
                                value={preorderForm.email}
                                onChange={(e) =>
                                    setPreorderForm({ ...preorderForm, email: e.target.value })
                                }
                                className="w-full bg-[#141414] border px-4 py-3 text-white"
                                style={{ borderColor: "rgba(212,175,55,0.2)" }}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <label className="eyebrow text-[10px] block">Edition</label>
                                <select
                                    data-testid="preorder-edition"
                                    value={preorderForm.edition}
                                    onChange={(e) =>
                                        setPreorderForm({
                                            ...preorderForm,
                                            edition: e.target.value,
                                        })
                                    }
                                    className="w-full bg-[#141414] border px-4 py-3 text-white"
                                    style={{ borderColor: "rgba(212,175,55,0.2)" }}
                                >
                                    <option value="collector">Collector (Numbered)</option>
                                    <option value="digital">Digital (Animated)</option>
                                </select>
                            </div>
                            <div className="space-y-1.5">
                                <label className="eyebrow text-[10px] block">Quantity</label>
                                <input
                                    data-testid="preorder-quantity"
                                    type="number"
                                    min={1}
                                    max={10}
                                    value={preorderForm.quantity}
                                    onChange={(e) =>
                                        setPreorderForm({
                                            ...preorderForm,
                                            quantity: parseInt(e.target.value || "1", 10),
                                        })
                                    }
                                    className="w-full bg-[#141414] border px-4 py-3 text-white"
                                    style={{ borderColor: "rgba(212,175,55,0.2)" }}
                                />
                            </div>
                        </div>

                        <button
                            data-testid="preorder-submit"
                            type="submit"
                            disabled={preorderState.type === "loading"}
                            className="btn-gold w-full justify-center disabled:opacity-50"
                        >
                            {preorderState.type === "loading"
                                ? "Etching…"
                                : "Reserve in the Ledger"}
                            <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                        {preorderState.msg && (
                            <p
                                data-testid="preorder-status"
                                className={`text-xs uppercase tracking-widest ${
                                    preorderState.type === "err"
                                        ? "text-[#cd6a6a]"
                                        : "text-[#D4AF37]"
                                }`}
                            >
                                {preorderState.msg}
                            </p>
                        )}
                    </form>
                </div>
            </section>
        </main>
    );
}
